/**
 * Manages the lifecycle and interactions for the sorting modal in the search results page.
 *
 * - Handles opening the sorting modal when the associated button is clicked.
 * - Creates and configures a new SortingModal instance, dispatches its state to the Redux store,
 *   and binds/unbinds its events as the modal is added or removed from the DOM.
 * - Listens for the modal close event to clean up and reset the modal state.
 *
 * This module ensures the sorting modal is properly initialized, displayed, and cleaned up
 * in response to user actions and application state changes.
 */

import { filter, fromEvent, merge } from 'rxjs';
import modalService from 'layout/services/modalService';
import { onOrientationChange$ } from 'layout/redux/observables';
import { store } from '@redux/store';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { ProductType, selectProductType, selectSorting, SortType } from 'features/searchResultsFacets';
import SortingModal from './sortingModal';

const sortingModalButtons = document.querySelectorAll('[data-sorting-modal-button]');
const onSortingModalButtonClick$ = sortingModalButtons.length > 0 
  ? merge(...Array.from(sortingModalButtons).map(btn => fromEvent(btn, 'click'))) 
  : null;
const onModalClosed$ = fromEvent(document.body, 'modalClosed');

const configuration = {
    modalInstance: null,
};

/**
 * Creates and initializes a new SortingModal instance for the sorting modal UI.
 *
 * - Instantiates a new SortingModal and stores the reference in the configuration object.
 * - Dispatches the modal's information to the Redux store for UI state tracking.
 * - Registers event handlers for when the modal's HTML is added to or removed from the DOM,
 *   binding and unbinding the modal's events as appropriate.
 * - Injects the modal's HTML into the DOM using the modal service.
 *
 * This function is responsible for setting up the sorting modal's lifecycle and ensuring
 * it is interactive and properly managed within the application's UI state.
 */
const createSortingModal = () => {
    const state = store.getState();
    const currentSort = SortType.fromKey(selectSorting(state)?.sortBy);

    configuration.modalInstance = new SortingModal();
    configuration.modalInstance.setMetadata({
        productType: ProductType.fromKey(selectProductType(state)),
        sorting: currentSort ? selectSorting(state) : null,
    });

    store.dispatch(uiStateSlice.actions.modalInformation({
        name: configuration.modalInstance.id,
    }));

    modalService.onHtmlReady({
        name: configuration.modalInstance.id,
        fn: configuration.modalInstance.bindEvents.bind(configuration.modalInstance),
    });

    modalService.onHtmlRemoved({
        name: configuration.modalInstance.id,
        fn: configuration.modalInstance.unbindEvents.bind(configuration.modalInstance),
    });

    modalService.setHtml({ payload: configuration.modalInstance.getHtml() });
};

/**
 * Cleans up and removes the current SortingModal instance from the configuration and Redux store.
 *
 * - Sets the modalInstance reference in the configuration object to null.
 * - Filters the modalClosed event to ensure it only processes events from the sorting modal.
 * - Dispatches the clearModal action to properly reset the Redux UI state.
 *
 * This function is typically called when the sorting modal is closed, ensuring
 * that the application's state accurately reflects the absence of an active modal
 * and prevents interference from unrelated modal close events.
 */
const onModalClosed = (event) => {
    const closedModalName = event?.detail?.modalInformation?.name;
    
    // Only handle the sorting modal's close event, ignore other modals
    if (closedModalName !== configuration.modalInstance?.id) {
        return;
    }
    
    configuration.modalInstance = null;
    store.dispatch(uiStateSlice.actions.clearModal());
};

onModalClosed$.subscribe(onModalClosed);
onSortingModalButtonClick$?.subscribe(createSortingModal);
onOrientationChange$.pipe(
  filter(() => configuration.modalInstance !== null)
).subscribe(modalService.clearHtml);